import { useEffect, useMemo, useRef, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useFormContext, useWatch, Controller, type Control } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { FormInput } from '@/components/forms/FormField';
import { useToast } from '@/components/ui/Toast';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useLookupValues } from '@/hooks/useLookupValues';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';
import { COUNTRY_SELECT_OPTIONS, resolveCountryCode } from './data/countries';
import { useVerifyPostalCode } from './hooks/usePostalCodeVerification';
import type {
  FormDefinitionFull,
  FormSection,
  FormField,
  FormFieldCondition,
} from './hooks/useFormBuilder';

// ─── Context for disabled categories ────────────────────────────────────────

const DisabledCategoriesContext = createContext<string[]>([]);

// ─── Validation helpers ──────────────────────────────────────────────────────

/** Reads a field's validation_rules, tolerating a JSON string or object. */
function readValidationRules(field: FormField): Record<string, unknown> {
  const raw = field.validation_rules as unknown;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return raw as Record<string, unknown>;
}

/**
 * Builds react-hook-form `rules` for a text-like field from its
 * validation_rules. Currently supports a `pattern` regex (with an optional
 * `pattern_message`). Returns undefined when there are no rules to apply.
 */
function buildTextFieldRules(field: FormField): Record<string, unknown> | undefined {
  const rules = readValidationRules(field);
  const pattern = typeof rules.pattern === 'string' ? rules.pattern.trim() : '';
  if (!pattern) return undefined;

  let regex: RegExp;
  try {
    regex = new RegExp(pattern);
  } catch {
    // Invalid regex stored on the field — skip rather than crash the form.
    return undefined;
  }

  const message =
    typeof rules.pattern_message === 'string' && rules.pattern_message.trim()
      ? rules.pattern_message.trim()
      : `${field.label} does not match the required format`;

  return { pattern: { value: regex, message } };
}

/**
 * Validates a single value against a field's regex pattern (if any).
 * Returns an error message string, or null when valid / not applicable.
 * Empty values are considered valid here — "required" is enforced separately.
 */
export function validateFieldPattern(field: FormField, value: unknown): string | null {
  const rules = readValidationRules(field);
  const pattern = typeof rules.pattern === 'string' ? rules.pattern.trim() : '';
  if (!pattern) return null;
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') return null;

  let regex: RegExp;
  try {
    regex = new RegExp(pattern);
  } catch {
    return null;
  }
  if (regex.test(value)) return null;

  return typeof rules.pattern_message === 'string' && rules.pattern_message.trim()
    ? rules.pattern_message.trim()
    : `${field.label} does not match the required format`;
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface DynamicFormRendererProps {
  formDefinition: FormDefinitionFull;
  /** Lookup categories that should be rendered as disabled/read-only (e.g. ['users_partner', 'users_manager']) */
  disabledCategories?: string[];
}

// ─── Main Renderer ──────────────────────────────────────────────────────────

/**
 * DynamicFormRenderer — renders a full form definition dynamically.
 *
 * Must be rendered inside a react-hook-form FormProvider context.
 *
 * Tab hierarchy:
 * - Sections with render_as='tab' are top-level tab containers
 * - Sections with parent_tab_id reference their parent tab
 * - Sections without parent_tab_id and not a tab → rendered directly (backward compat)
 *
 * Within each tab, child sections render as:
 * - 'section' → Card with header
 * - 'accordion' → collapsible accordion panels
 */
export function DynamicFormRenderer({
  formDefinition,
  disabledCategories,
}: DynamicFormRendererProps) {
  const sections = formDefinition.sections;
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    () => new Set(sections?.filter((s) => s.render_as === 'accordion').map((s) => s.id) ?? []),
  );

  if (!sections || sections.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="py-4 text-sm text-gray-500">No form sections configured.</p>
        </CardContent>
      </Card>
    );
  }

  // Identify top-level tabs — DISABLED when parent manages tabs
  // The parent (ContactForm/JobForm) handles tab switching and only passes
  // the relevant subset of sections to render. The renderer just renders sections directly.

  // All sections are treated as orphans (rendered directly)
  const orphanSections = sections.filter((s) => s.render_as !== 'tab' && s.is_active !== false);

  // Include tab sections that have fields (rendered as regular sections)
  const tabSectionsWithFields = sections.filter(
    (s) => s.render_as === 'tab' && s.fields.filter((f) => f.is_active).length > 0,
  );

  // Combine: tab sections (as cards) + orphan sections
  const allRenderable = [
    ...tabSectionsWithFields.map((s) => ({ ...s, render_as: 'section' as const })),
    ...orphanSections,
  ].sort((a, b) => a.display_order - b.display_order);

  const toggleAccordion = (id: string) => {
    setExpandedAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <DisabledCategoriesContext.Provider value={disabledCategories ?? []}>
      <div className="space-y-6">
        {/* Render all sections as cards or accordions — no internal tabs */}
        {allRenderable.map((section) =>
          section.render_as === 'accordion' ? (
            <div
              key={section.id}
              className="rounded-lg border border-border dark:border-border-dark overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleAccordion(section.id)}
                className="flex w-full items-center justify-between px-4 py-3 bg-gray-50 dark:bg-surface-dark-secondary hover:bg-gray-100 dark:hover:bg-surface-dark-tertiary transition-colors"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {section.name}
                </span>
                <svg
                  className={`h-4 w-4 text-gray-500 transition-transform ${expandedAccordions.has(section.id) ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedAccordions.has(section.id) && (
                <div className="px-4 py-4">
                  <SectionFields section={section} />
                </div>
              )}
            </div>
          ) : (
            <DynamicSection key={section.id} section={section} />
          ),
        )}

        {/* Empty state */}
        {allRenderable.length === 0 && (
          <Card>
            <CardContent>
              <p className="py-4 text-sm text-gray-500">No fields to display.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DisabledCategoriesContext.Provider>
  );
}

// ─── Section Fields (shared renderer for fields grid) ────────────────────────

function SectionFields({ section }: { section: FormSection }) {
  const visibleFields = section.fields.filter((f) => f.is_active);
  if (visibleFields.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {visibleFields.map((field) => (
        <ConditionalField key={field.id} field={field} allFields={section.fields} />
      ))}
    </div>
  );
}

// ─── Card Section Renderer ───────────────────────────────────────────────────

function DynamicSection({ section }: { section: FormSection }) {
  const visibleFields = section.fields.filter((f) => f.is_active);

  if (visibleFields.length === 0) return null;

  // Tab sections don't show header — title is already on the tab button
  if (section.render_as === 'tab') {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {visibleFields.map((field) => (
          <ConditionalField key={field.id} field={field} allFields={section.fields} />
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader title={section.name} description={section.description ?? undefined} />
      <CardContent>
        <SectionFields section={section} />
      </CardContent>
    </Card>
  );
}

// ─── Conditional Wrapper ─────────────────────────────────────────────────────

function ConditionalField({ field, allFields }: { field: FormField; allFields: FormField[] }) {
  const { watch } = useFormContext();

  // Evaluate conditions — if any conditions exist, check them
  const isVisible = useMemo(() => {
    if (!field.conditions || field.conditions.length === 0) return true;
    return evaluateConditions(field.conditions, watch, allFields);
  }, [field.conditions, watch, allFields]);

  if (!isVisible) return null;

  const widthClass = field.width === 'full' ? 'sm:col-span-2' : field.width === 'third' ? '' : '';

  return (
    <div className={widthClass}>
      <DynamicField field={field} />
    </div>
  );
}

function evaluateConditions(
  conditions: FormFieldCondition[],
  watch: (name: string) => unknown,
  allFields: FormField[],
): boolean {
  if (conditions.length === 0) return true;

  const results = conditions.map((cond) => {
    const depField = allFields.find((f) => f.id === cond.depends_on_field_id);
    if (!depField) return true;

    const fieldName =
      depField.is_system_field && depField.db_column
        ? camelCase(depField.db_column)
        : `customFields.${depField.field_key}`;

    const currentValue = watch(fieldName);
    return evaluateSingleCondition(currentValue, cond.operator, cond.value);
  });

  // Check logic groups
  const hasOr = conditions.some((c) => c.logic_group === 'OR');
  if (hasOr) return results.some((r) => r);
  return results.every((r) => r);
}

function evaluateSingleCondition(current: unknown, operator: string, expected: unknown): boolean {
  const strCurrent = String(current ?? '');
  const strExpected = String(expected ?? '');

  switch (operator) {
    case 'eq':
      return strCurrent === strExpected;
    case 'neq':
      return strCurrent !== strExpected;
    case 'in':
      return Array.isArray(expected) ? expected.includes(strCurrent) : strCurrent === strExpected;
    case 'not_in':
      return Array.isArray(expected) ? !expected.includes(strCurrent) : strCurrent !== strExpected;
    case 'is_empty':
      return !current || strCurrent === '';
    case 'is_not_empty':
      return !!current && strCurrent !== '';
    default:
      return true;
  }
}

function camelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// ─── Field Renderer ──────────────────────────────────────────────────────────

function DynamicField({ field }: { field: FormField }) {
  const { control } = useFormContext();
  const disabledCategories = useContext(DisabledCategoriesContext);

  // Determine the form field name
  const fieldName =
    field.is_system_field && field.db_column
      ? camelCase(field.db_column)
      : `customFields.${field.field_key}`;

  // Client code is auto-generated and system-managed — render read-only so it
  // can be displayed on the form but never edited by hand.
  if (field.is_system_field && field.db_column === 'client_code') {
    return (
      <Controller
        name={fieldName}
        control={control as never}
        defaultValue=""
        render={({ field: formField }) => (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.label}
            </label>
            <input
              type="text"
              value={(formField.value as string) || ''}
              readOnly
              disabled
              placeholder="Auto-generated on save"
              className="block w-full cursor-not-allowed rounded-lg border border-border bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600 dark:border-border-dark dark:bg-surface-dark-tertiary dark:text-gray-400"
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Generated automatically from the entity type.
            </p>
          </div>
        )}
      />
    );
  }

  // Country dropdown (standard ISO country list)
  if (field.field_type === 'country') {
    return (
      <SearchableSelect
        fieldName={fieldName}
        control={control}
        label={field.label}
        required={field.is_required}
        options={COUNTRY_SELECT_OPTIONS}
        placeholder={field.placeholder ?? 'Select country'}
      />
    );
  }

  // Postal code with third-party verification.
  if (field.field_type === 'postal_code') {
    return <PostalCodeField fieldName={fieldName} field={field} control={control} />;
  }

  // System user picker — dropdown of active users, filtered by the roles
  // configured on the field (validation_rules.roles).
  if (field.field_type === 'user_select') {
    return <UserSelectField fieldName={fieldName} field={field} control={control} />;
  }

  // Client/contact fields use a searchable modal (respects data authorization
  // via the /contacts endpoint) instead of a bounded dropdown.
  if (
    ['select', 'multi_select'].includes(field.field_type) &&
    field.lookup_category === 'contacts'
  ) {
    const isDisabled = disabledCategories.includes('contacts');
    return (
      <ContactLookupField
        fieldName={fieldName}
        field={field}
        control={control}
        disabled={isDisabled}
      />
    );
  }

  // For select/multi_select with lookup_category, use the LookupSelect
  if (['select', 'multi_select'].includes(field.field_type) && field.lookup_category) {
    const isDisabled = disabledCategories.includes(field.lookup_category);
    return (
      <LookupSelect fieldName={fieldName} field={field} control={control} disabled={isDisabled} />
    );
  }

  // For select/radio with static options — use searchable dropdown
  if (['select', 'radio'].includes(field.field_type) && field.options.length > 0) {
    return (
      <SearchableSelect
        fieldName={fieldName}
        control={control}
        label={field.label}
        required={field.is_required}
        options={field.options}
        placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`}
      />
    );
  }

  // For multi_select with static options
  if (field.field_type === 'multi_select' && field.options.length > 0) {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={[]}
        render={({ field: formField }) => {
          const selected: string[] = Array.isArray(formField.value) ? formField.value : [];
          return (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}
                {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <div className="space-y-2 rounded-lg border border-border p-3 dark:border-border-dark max-h-40 overflow-y-auto">
                {field.options.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...selected, opt.value]
                          : selected.filter((v) => v !== opt.value);
                        formField.onChange(next);
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {field.hint_text && <p className="mt-1 text-xs text-gray-500">{field.hint_text}</p>}
            </div>
          );
        }}
      />
    );
  }

  // Checkbox
  if (field.field_type === 'checkbox') {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={false}
        render={({ field: formField }) => (
          <div className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              id={fieldName}
              checked={!!formField.value}
              onChange={(e) => formField.onChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600"
            />
            <label htmlFor={fieldName} className="text-sm text-gray-700 dark:text-gray-300">
              {field.label}
            </label>
          </div>
        )}
      />
    );
  }

  // Textarea
  if (field.field_type === 'textarea') {
    return (
      <Controller
        name={fieldName}
        control={control as never}
        defaultValue=""
        rules={buildTextFieldRules(field) as never}
        render={({ field: formField, fieldState: { error } }) => (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.label}
              {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            <textarea
              value={(formField.value as string) ?? ''}
              onChange={formField.onChange}
              onBlur={formField.onBlur}
              placeholder={field.placeholder ?? ''}
              rows={4}
              aria-invalid={error ? 'true' : undefined}
              className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100 ${
                error
                  ? 'border-danger-500 focus:ring-danger-500'
                  : 'border-border dark:border-border-dark'
              }`}
            />
            {error ? (
              <p className="mt-1 text-sm text-danger-500" role="alert">
                {error.message}
              </p>
            ) : (
              field.hint_text && <p className="mt-1 text-xs text-gray-500">{field.hint_text}</p>
            )}
          </div>
        )}
      />
    );
  }

  // File upload field
  if (field.field_type === 'file') {
    return <FileUploadField fieldName={fieldName} field={field} control={control} />;
  }

  // Table (repeatable rows) field
  if (field.field_type === 'table') {
    return <TableField fieldName={fieldName} field={field} control={control} />;
  }

  // Default: text, number, date, email, phone → use FormInput
  const inputType =
    field.field_type === 'number'
      ? 'number'
      : field.field_type === 'date'
        ? 'date'
        : field.field_type === 'email'
          ? 'email'
          : field.field_type === 'phone'
            ? 'tel'
            : 'text';

  // Apply a regex format (validation_rules.pattern) when defined — mainly for
  // text/email/phone. react-hook-form surfaces the error via FormInput.
  const patternRules = buildTextFieldRules(field);

  return (
    <FormInput
      name={fieldName}
      control={control as never}
      label={field.label}
      required={field.is_required}
      type={inputType}
      placeholder={field.placeholder ?? undefined}
      hint={field.hint_text ?? undefined}
      rules={patternRules as never}
    />
  );
}

// ─── Lookup-backed Select ────────────────────────────────────────────────────

// ─── Contact Lookup (searchable modal, data-authorized) ──────────────────────

function ContactLookupField({
  fieldName,
  field,
  control,
  disabled,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
  disabled?: boolean;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Local cache of id -> display name for chosen/known contacts, so the field
  // shows a name rather than a raw id. Prefilled/edit values are resolved from
  // the bounded contacts lookup as a fallback.
  const { options } = useLookupValues('contacts');
  const [selectedName, setSelectedName] = useState<string | null>(null);

  return (
    <Controller
      name={fieldName}
      control={control as never}
      render={({ field: formField }) => {
        const currentId = (formField.value as string) || '';
        const labelFromOptions = options.find((o) => o.value === currentId)?.label;
        const displayName = selectedName || labelFromOptions || '';

        const handleSelect = (client: ClientSearchResult) => {
          formField.onChange(client.id);
          setSelectedName(client.displayName);
          setIsModalOpen(false);
        };

        return (
          <div>
            {field.label && (
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}
                {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
              </label>
            )}

            {currentId && displayName ? (
              <div className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-700 dark:bg-brand-900/20">
                <span className="flex-1 truncate text-sm font-medium text-brand-700 dark:text-brand-300">
                  {displayName}
                </span>
                {!disabled && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(true)}
                      className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        formField.onChange('');
                        setSelectedName(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear selected client"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                disabled={disabled}
                className="flex w-full items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-left text-sm text-gray-500 transition-colors hover:border-brand-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-400"
              >
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {field.placeholder ?? 'Search & select a client...'}
              </button>
            )}

            {field.hint_text && <p className="mt-1 text-xs text-gray-500">{field.hint_text}</p>}

            {isModalOpen && (
              <ClientSearchModal
                title="Search & Select Client"
                contactNature="client"
                onSelect={handleSelect}
                onClose={() => setIsModalOpen(false)}
              />
            )}
          </div>
        );
      }}
    />
  );
}

function LookupSelect({
  fieldName,
  field,
  control,
  disabled,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
  disabled?: boolean;
}) {
  const { options, isLoading } = useLookupValues(field.lookup_category);

  if (isLoading) {
    return (
      <div>
        {field.label && (
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {field.label}
            {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        <div className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-400 dark:border-border-dark dark:bg-surface-dark-secondary">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <SearchableSelect
      fieldName={fieldName}
      control={control}
      label={field.label}
      required={field.is_required}
      options={options}
      placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`}
      disabled={disabled}
    />
  );
}

// ─── System User Select (role-filtered) ──────────────────────────────────────

/**
 * Renders a dropdown of active system users, filtered by the roles configured
 * on the field (validation_rules.roles — an array of RBAC role ids). An empty
 * roles list includes all users. Stores the selected user's id; the id→name
 * label resolves automatically from the fetched options (edit mode).
 */
function UserSelectField({
  fieldName,
  field,
  control,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
}) {
  const rules = readValidationRules(field);
  const roleIds = Array.isArray(rules.roles) ? (rules.roles as string[]).filter(Boolean) : [];

  const { data: users, isLoading } = useQuery({
    queryKey: ['user-select-field', roleIds.slice().sort().join(',')],
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: '200', status: 'active' });
      if (roleIds.length > 0) params.set('role_ids', roleIds.join(','));
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/users?${params.toString()}`,
      );
      return response.data.data ?? [];
    },
    staleTime: 2 * 60_000,
  });

  const options = (users ?? []).map((u) => ({
    value: u.id as string,
    label: (u.name || u.email || u.id) as string,
  }));

  if (isLoading) {
    return (
      <div>
        {field.label && (
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {field.label}
            {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        <div className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-400 dark:border-border-dark dark:bg-surface-dark-secondary">
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <SearchableSelect
      fieldName={fieldName}
      control={control}
      label={field.label}
      required={field.is_required}
      options={options}
      placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`}
    />
  );
}

// ─── Searchable Select Component ─────────────────────────────────────────────

function SearchableSelect({
  fieldName,
  control,
  label,
  required,
  options,
  placeholder,
  disabled,
}: {
  fieldName: string;
  control: unknown;
  label: string;
  required: boolean;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  // The wrapper around the input — used to measure where to anchor the
  // portaled dropdown so no ancestor `overflow` (tab panels, cards) can clip it.
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, search]);

  // Recompute the dropdown's screen position whenever it opens, and keep it in
  // sync while scrolling/resizing so it stays glued to the input.
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuRect({ top: rect.bottom, left: rect.left, width: rect.width });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  return (
    <Controller
      name={fieldName}
      control={control as never}
      render={({ field: formField }) => {
        const selectedLabel = options.find((o) => o.value === formField.value)?.label || '';

        return (
          <div className="relative">
            {label && (
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
                {required && <span className="ml-0.5 text-red-500">*</span>}
              </label>
            )}
            <div className="relative" ref={anchorRef}>
              <input
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                value={isOpen ? search : selectedLabel}
                onChange={(e) => {
                  if (disabled) return;
                  setSearch(e.target.value);
                  if (!isOpen) setIsOpen(true);
                }}
                onFocus={() => {
                  if (disabled) return;
                  setIsOpen(true);
                  setSearch('');
                }}
                onBlur={() => {
                  // Delay to allow click on option
                  setTimeout(() => setIsOpen(false), 200);
                }}
                placeholder={placeholder}
                disabled={disabled}
                className={`block w-full cursor-pointer rounded-lg border border-border bg-white px-3 py-2 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder:text-gray-500${disabled ? ' opacity-50 cursor-not-allowed' : ''}`}
                autoComplete="off"
              />
              {/* Dropdown chevron — signals this is a selectable field */}
              <svg
                className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {/* Dropdown is portaled to <body> and positioned via fixed coords so
                it's never clipped by an ancestor with overflow hidden/auto
                (tab panels, cards). max-h-72 shows ~8 rows then scrolls. */}
            {isOpen &&
              !disabled &&
              menuRect &&
              filteredOptions.length > 0 &&
              createPortal(
                <ul
                  className="fixed z-[1000] mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-surface-dark-secondary"
                  style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
                  role="listbox"
                >
                  {filteredOptions.map((opt) => (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={formField.value === opt.value}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        formField.onChange(opt.value);
                        setSearch('');
                        setIsOpen(false);
                      }}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-brand-50 dark:hover:bg-brand-900/20 ${
                        formField.value === opt.value
                          ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>,
                document.body,
              )}
            {isOpen &&
              menuRect &&
              filteredOptions.length === 0 &&
              search.trim() &&
              createPortal(
                <div
                  className="fixed z-[1000] mt-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-400 shadow-lg dark:border-border-dark dark:bg-surface-dark-secondary"
                  style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
                >
                  No results found
                </div>,
                document.body,
              )}
          </div>
        );
      }}
    />
  );
}

// ─── Postal Code Field (third-party verification) ────────────────────────────

/**
 * Postal/ZIP code input with a Verify action that checks the code against the
 * backend verification proxy for the sibling country (defaults to Canada when
 * no country field is present). Validation only — the value is not modified.
 */
function PostalCodeField({
  fieldName,
  field,
  control,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
}) {
  const countryValue = useWatch({ control: control as Control, name: 'country' }) as
    | string
    | undefined;
  const verify = useVerifyPostalCode();
  const [status, setStatus] = useState<'idle' | 'verifying' | 'valid' | 'invalid' | 'error'>(
    'idle',
  );
  const [detail, setDetail] = useState<string>('');
  // Remembers the last "country|postalCode" we verified so leaving the field
  // without changing it doesn't trigger a redundant provider call.
  const lastVerifiedRef = useRef<string>('');

  const countryCode = resolveCountryCode(countryValue) ?? 'CA';

  return (
    <Controller
      name={fieldName}
      control={control as never}
      defaultValue=""
      render={({ field: formField }) => {
        const value = (formField.value as string) || '';

        const runVerify = async () => {
          const trimmed = value.trim();
          if (!trimmed) return;
          const key = `${countryCode}|${trimmed.toUpperCase()}`;
          // Skip if we've already verified this exact value/country.
          if (key === lastVerifiedRef.current && status !== 'idle' && status !== 'verifying') {
            return;
          }
          lastVerifiedRef.current = key;
          setStatus('verifying');
          setDetail('Verifying…');
          try {
            const result = await verify.mutateAsync({
              country: countryCode,
              postalCode: value,
            });
            if (result.valid) {
              setStatus('valid');
              const place = result.places[0];
              setDetail(
                place
                  ? `${place.placeName}${place.state ? `, ${place.state}` : ''}`
                  : 'Postal code is valid',
              );
            } else {
              setStatus('invalid');
              setDetail('Not a valid postal code for the selected country');
            }
          } catch {
            setStatus('error');
            setDetail('Could not verify right now. Please try again.');
          }
        };

        return (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.label}
              {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => {
                formField.onChange(e.target.value);
                // Reset any previous result as the user edits.
                if (status !== 'idle') {
                  setStatus('idle');
                  setDetail('');
                }
              }}
              onBlur={() => {
                formField.onBlur();
                // Verify automatically when focus leaves the field.
                void runVerify();
              }}
              placeholder={field.placeholder ?? 'Postal / ZIP code'}
              className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder:text-gray-500"
              autoComplete="off"
            />
            {status !== 'idle' && (
              <p
                className={`mt-1 text-xs ${
                  status === 'valid'
                    ? 'text-green-600 dark:text-green-400'
                    : status === 'invalid'
                      ? 'text-red-600 dark:text-red-400'
                      : status === 'error'
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-500 dark:text-gray-400'
                }`}
                role="status"
              >
                {status === 'valid' ? '✓ ' : status === 'invalid' ? '✕ ' : ''}
                {detail}
              </p>
            )}
            {field.hint_text && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.hint_text}</p>
            )}
          </div>
        );
      }}
    />
  );
}

// ─── File Upload Field ───────────────────────────────────────────────────────

function FileUploadField({
  fieldName,
  field,
  control,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  // File field config from validation_rules
  const config = field.validation_rules as Record<string, boolean> | null;
  const allowPreview = config?.allow_preview !== false;
  const allowDownload = config?.allow_download !== false;
  const allowReplace = config?.allow_replace !== false;
  const allowAttach = config?.allow_attach !== false;

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ApiResponse<{ id: string; file_name: string }>>(
        '/documents/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      onChange(response.data.data.id);
      toast({ variant: 'success', title: `File "${file.name}" uploaded` });
    } catch {
      toast({ variant: 'error', title: 'Failed to upload file' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePreview = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/preview`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart], { type: 'application/octet-stream' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast({ variant: 'error', title: 'Failed to preview file' });
    }
  };

  const handleDownload = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = field.label || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: 'error', title: 'Failed to download file' });
    }
  };

  return (
    <Controller
      name={fieldName}
      control={control as never}
      defaultValue=""
      render={({ field: formField }) => {
        const documentId = formField.value as string;
        const hasFile = !!documentId;

        return (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.label}
              {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
            </label>

            {hasFile ? (
              <div className="flex items-center gap-2 rounded-lg border border-border p-3 dark:border-border-dark">
                <svg
                  className="h-5 w-5 text-success-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                  File attached
                </span>
                <div className="flex items-center gap-1">
                  {allowPreview && (
                    <button
                      type="button"
                      onClick={() => handlePreview(documentId)}
                      className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                      title="Preview"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  )}
                  {allowDownload && (
                    <button
                      type="button"
                      onClick={() => handleDownload(documentId)}
                      className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                      title="Download"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                        />
                      </svg>
                    </button>
                  )}
                  {allowReplace && (
                    <>
                      <input
                        type="file"
                        onChange={(e) => handleUpload(e, formField.onChange)}
                        className="hidden"
                        id={`replace-${field.id}`}
                        disabled={uploading}
                      />
                      <label
                        htmlFor={`replace-${field.id}`}
                        className="cursor-pointer rounded p-1.5 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400"
                        title="Replace file"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                          />
                        </svg>
                      </label>
                    </>
                  )}
                </div>
              </div>
            ) : allowAttach ? (
              <div>
                <input
                  type="file"
                  onChange={(e) => handleUpload(e, formField.onChange)}
                  className="hidden"
                  id={`upload-${field.id}`}
                  disabled={uploading}
                />
                <label
                  htmlFor={`upload-${field.id}`}
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 text-sm transition-colors ${
                    uploading
                      ? 'border-gray-200 text-gray-400 dark:border-gray-700'
                      : 'border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-brand-500 dark:hover:text-brand-400'
                  }`}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  {uploading ? 'Uploading...' : 'Attach File'}
                </label>
                {field.hint_text && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.hint_text}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">File upload not enabled for this field.</p>
            )}
          </div>
        );
      }}
    />
  );
}

// ─── Table (Repeatable Rows) Field ───────────────────────────────────────────

/**
 * Renders a table field with repeatable rows.
 * Columns are defined in field.options: [{ value: 'column_key', label: 'Column Label' }]
 * Each column's type can be specified in validation_rules.column_types: { column_key: 'text'|'number'|'date'|'select' }
 * Data stored as JSON array: [{ col1: 'val', col2: 'val' }, ...]
 */
function TableField({
  fieldName,
  field,
  control,
}: {
  fieldName: string;
  field: FormField;
  control: unknown;
}) {
  const columns = field.options || [];
  const columnTypes = (field.validation_rules as Record<string, unknown>)?.column_types as
    | Record<string, string>
    | undefined;
  const columnLookups = (field.validation_rules as Record<string, unknown>)?.column_lookups as
    | Record<string, string>
    | undefined;

  // Also fetch field library to resolve column metadata for fields picked from library
  const { data: libraryFields } = useQuery({
    queryKey: ['field-library-for-table'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        '/form-builder/field-library',
      );
      return response.data.data ?? [];
    },
    staleTime: 5 * 60_000,
  });

  // Build effective column config by merging validation_rules with field library data
  const getColumnType = (colKey: string): string => {
    if (columnTypes?.[colKey]) return columnTypes[colKey];
    const libField = libraryFields?.find((f) => f.field_key === colKey);
    if (libField) return libField.field_type as string;
    return 'text';
  };

  const getColumnLookup = (colKey: string): string | undefined => {
    if (columnLookups?.[colKey]) return columnLookups[colKey];
    const libField = libraryFields?.find((f) => f.field_key === colKey);
    if (libField?.lookup_category) return libField.lookup_category as string;
    return undefined;
  };

  const getColumnOptions = (
    colKey: string,
  ): Array<{ value: string; label: string }> | undefined => {
    const colOptions = (
      (field.validation_rules as Record<string, unknown>)?.column_options as
        | Record<string, Array<{ value: string; label: string }>>
        | undefined
    )?.[colKey];
    if (colOptions) return colOptions;
    const libField = libraryFields?.find((f) => f.field_key === colKey);
    if (libField?.options) {
      const opts = libField.options;
      if (Array.isArray(opts) && opts.length > 0)
        return opts as Array<{ value: string; label: string }>;
      if (typeof opts === 'string') {
        try {
          const parsed = JSON.parse(opts);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          /* ignore */
        }
      }
    }
    return undefined;
  };

  const columnRequired = (field.validation_rules as Record<string, unknown>)?.column_required as
    | Record<string, boolean>
    | undefined;
  const columnUnique = (field.validation_rules as Record<string, unknown>)?.column_unique as
    | Record<string, boolean>
    | undefined;

  return (
    <Controller
      name={fieldName}
      control={control as never}
      defaultValue={[]}
      render={({ field: formField }) => {
        const rows: Record<string, string>[] = Array.isArray(formField.value)
          ? formField.value
          : [];

        const addRow = () => {
          const emptyRow: Record<string, string> = {};
          for (const col of columns) {
            emptyRow[col.value] = '';
          }
          formField.onChange([...rows, emptyRow]);
        };

        const removeRow = (index: number) => {
          formField.onChange(rows.filter((_, i) => i !== index));
        };

        const updateCell = (rowIndex: number, colKey: string, value: string) => {
          const updated = rows.map((row, i) =>
            i === rowIndex ? { ...row, [colKey]: value } : row,
          );
          formField.onChange(updated);
        };

        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}
                {field.is_required && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <button
                type="button"
                onClick={addRow}
                className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Row
              </button>
            </div>

            {field.hint_text && (
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{field.hint_text}</p>
            )}

            {columns.length === 0 ? (
              <p className="text-sm text-gray-400">No columns defined for this table field.</p>
            ) : rows.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center text-sm text-gray-400 dark:border-gray-700">
                No rows. Click "Add Row" to begin.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border dark:border-border-dark">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-gray-50 dark:border-border-dark dark:bg-surface-dark-tertiary">
                      {columns.map((col) => (
                        <th
                          key={col.value}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          {col.label}
                          {columnRequired?.[col.value] && (
                            <span className="ml-0.5 text-red-500">*</span>
                          )}
                          {columnUnique?.[col.value] && (
                            <span className="ml-1 text-xs text-gray-400" title="Must be unique">
                              ⊘
                            </span>
                          )}
                        </th>
                      ))}
                      <th className="w-10 px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border-b border-border last:border-b-0 dark:border-border-dark"
                      >
                        {columns.map((col) => {
                          const colType = getColumnType(col.value);
                          const colLookup = getColumnLookup(col.value);
                          const colOptions = getColumnOptions(col.value);
                          const isColRequired = columnRequired?.[col.value] ?? false;
                          const isColUnique = columnUnique?.[col.value] ?? false;
                          const cellValue = row[col.value] || '';
                          const isEmpty = isColRequired && !cellValue;
                          const isDuplicate =
                            isColUnique &&
                            cellValue &&
                            rows.filter((r) => r[col.value] === cellValue).length > 1;

                          return (
                            <td key={col.value} className="px-2 py-1.5">
                              {colType === 'select' && (colLookup || colOptions) ? (
                                <TableCellSelect
                                  value={cellValue}
                                  onChange={(val) => updateCell(rowIndex, col.value, val)}
                                  lookupCategory={colLookup}
                                  staticOptions={colOptions}
                                  placeholder={col.label}
                                />
                              ) : (
                                <input
                                  type={
                                    colType === 'number'
                                      ? 'number'
                                      : colType === 'date'
                                        ? 'date'
                                        : 'text'
                                  }
                                  value={cellValue}
                                  onChange={(e) => updateCell(rowIndex, col.value, e.target.value)}
                                  className={`w-full rounded border px-2 py-1 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:bg-surface-dark-secondary dark:text-gray-100 ${
                                    isEmpty || isDuplicate
                                      ? 'border-red-400 bg-red-50 dark:border-red-500 dark:bg-red-900/10'
                                      : 'border-border bg-white dark:border-border-dark'
                                  }`}
                                  placeholder={col.label}
                                  title={
                                    isEmpty
                                      ? 'This field is required'
                                      : isDuplicate
                                        ? 'Value must be unique'
                                        : undefined
                                  }
                                />
                              )}
                              {isDuplicate && (
                                <p className="mt-0.5 text-[10px] text-red-500">Duplicate</p>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            onClick={() => removeRow(rowIndex)}
                            className="rounded p-1 text-gray-400 hover:text-danger-500"
                            title="Remove row"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

// ─── Table Cell Select (for dropdown columns in table fields) ────────────────

function TableCellSelect({
  value,
  onChange,
  lookupCategory,
  staticOptions,
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  lookupCategory?: string;
  staticOptions?: Array<{ value: string; label: string }>;
  placeholder: string;
}) {
  const { options: resolvedOptions } = useLookupValues(lookupCategory, {
    staticOptions,
  });

  const options = staticOptions && staticOptions.length > 0 ? staticOptions : resolvedOptions;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-border bg-white px-2 py-1 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
